import { useMutation, useQuery } from '@apollo/client';
import React, { useState, useCallback } from 'react';
import { WrappedSpinner } from 'spinner';
import { formatCentsAsDollars, NotificationMessage, toastError } from 'bench-internal-theme';
import qs from 'qs';
import { getClientId, mapAddOnsToOptions, mapDataWithIdAndNameToOptions } from 'bk-web-app';
import { UPDATE_JOURNAL_ENTRY, GET_JOURNAL_ENTRY_FORM_VALUES, GET_JOURNAL_ENTRY } from './queries';
import JournalEntryForm from './JournalEntryForm';
import { getJournalEntries1099RefetchQuery } from '../bk-web-app/transaction-manager/components/transactions-1099-container/graphql';

interface DataEntryInput {
  id: string;
  ledgerAccountId: string;
  debitAmount?: string;
  creditAmount?: string;
}

interface ProcessedDataEntry {
  id: string;
  ledgerAccountId: string;
  amountInCents: number;
}

const getDataEntryInputs = (dataEntries: DataEntryInput[]): ProcessedDataEntry[] =>
  dataEntries.map(dataEntry => {
    const { id, ledgerAccountId, debitAmount, creditAmount } = dataEntry;
    const areBothDebitAndCreditSet = !!debitAmount && !!creditAmount;

    let amountInCents: number;
    if (areBothDebitAndCreditSet) {
      amountInCents = NaN;
    } else {
      amountInCents = Number(debitAmount) || Number(`-${creditAmount}`);
    }

    return {
      id,
      ledgerAccountId,
      amountInCents,
    };
  });

const validateDataEntryAmounts = (dataEntries: ProcessedDataEntry[]): boolean =>
  dataEntries.some(dataEntry => !dataEntry.amountInCents);

interface FormValues {
  addOnId?: string;
  vendorName: string;
  dateOfTransaction: string;
  dataEntries: DataEntryInput[];
}

/**
 * Generates an object that represents the data in the provided form element. It uses query string syntax
 * to support nested properties in the form. Also, it uses {@link https://www.npmjs.com/package/qs}
 * to parse the querystring.
 */
const getFormValues = (htmlFormElement: HTMLFormElement): FormValues => {
  const formValuesQuerystring = Array.from(htmlFormElement as any)
    .map((el: any) => `${el.name}=${encodeURIComponent(el.value)}`)
    .join('&');

  return qs.parse(formValuesQuerystring) as unknown as FormValues;
};

const handleUpdateJournalEntryError = (): void =>
  toastError(
    'Unable to update journal entry',
    'An error occurred when trying to update this journal entry. It might work if you give it another try.',
  );

interface JournalEntryFormContainerProps {
  journalEntryId: string;
  onComplete: () => void;
}

const JournalEntryFormContainer: React.FC<JournalEntryFormContainerProps> = ({
  journalEntryId,
  onComplete,
}) => {
  const [dataEntriesErrorMessage, setDataEntriesErrorMessage] = useState<string>('');
  const clientId = getClientId();

  // Use regular useQuery for v1 GraphQL (legacy endpoint)
  const {
    loading: getJournalEntryFormLoading,
    error: getJournalEntryFormError,
    data: getJournalEntryFormData,
  } = useQuery(GET_JOURNAL_ENTRY_FORM_VALUES, {
    variables: {
      journalEntryId,
      clientId,
    },
  });

  const [updateJournalEntry, { loading: updateJournalEntryLoading }] = useMutation(
    UPDATE_JOURNAL_ENTRY,
    {
      refetchQueries: [
        ...getJournalEntries1099RefetchQuery(clientId),
        {
          query: GET_JOURNAL_ENTRY,
          variables: { id: journalEntryId },
        },
      ],
      onCompleted: onComplete,
      onError: handleUpdateJournalEntryError,
      awaitRefetchQueries: true,
    },
  );

  const handleFormSubmit = useCallback(
    (submitEvent: React.FormEvent<HTMLFormElement>) => {
      submitEvent.preventDefault();
      const newJournalEntryValues = getFormValues(submitEvent.currentTarget);
      const dataEntries = getDataEntryInputs(newJournalEntryValues.dataEntries);
      const dataEntriesHaveInvalidAmounts = validateDataEntryAmounts(dataEntries);

      if (dataEntriesHaveInvalidAmounts) {
        setDataEntriesErrorMessage(
          'Data entry amounts are invalid. Review these amounts and try again.',
        );
        return;
      }

      const dataEntriesTotalBalance = dataEntries.reduce(
        (total, { amountInCents }) => total + amountInCents,
        0,
      );

      if (dataEntriesTotalBalance !== 0) {
        setDataEntriesErrorMessage(
          `Debits and credits are off by $${formatCentsAsDollars(
            Math.abs(dataEntriesTotalBalance),
          )}.`,
        );
        return;
      }

      const journalEntryInput = {
        addOnId: newJournalEntryValues.addOnId ? newJournalEntryValues.addOnId : null,
        vendorName: newJournalEntryValues.vendorName.trim(),
        dateOfTransaction: newJournalEntryValues.dateOfTransaction,
        dataEntries,
      };

      console.log('update');

      updateJournalEntry({
        variables: {
          journalEntryId,
          journalEntryInput,
        },
      });
    },
    [journalEntryId, updateJournalEntry],
  );

  if (getJournalEntryFormLoading) {
    return <WrappedSpinner />;
  }

  if (getJournalEntryFormError) {
    return (
      <NotificationMessage styleType="error">
        There was an error editing this journal entry
      </NotificationMessage>
    );
  }

  const { vendorName, dataEntries, dateOfTransaction, addOn } =
    getJournalEntryFormData.journalEntry;

  const ledgerOptions = mapDataWithIdAndNameToOptions(getJournalEntryFormData.ledgerAccounts);
  const dataEntriesHaveValidLedgers = dataEntries.every(
    ({ ledgerAccount }: any) =>
      ledgerAccount && ledgerOptions.find((option: any) => option.value === ledgerAccount.id),
  );

  if (!dataEntriesHaveValidLedgers) {
    return (
      <NotificationMessage styleType="error">
        Data entries in this journal entry point to ledgers that do not exist for this client.
      </NotificationMessage>
    );
  }

  const addOnOptions = mapAddOnsToOptions(getJournalEntryFormData.addOns);

  return (
    <JournalEntryForm
      dataEntriesErrorMessage={dataEntriesErrorMessage}
      defaultAddOnId={addOn && addOn.id}
      defaultDataEntries={dataEntries}
      defaultDateOfTransaction={dateOfTransaction}
      defaultVendorName={vendorName}
      isSubmitting={updateJournalEntryLoading}
      ledgerOptions={ledgerOptions}
      addOnOptions={addOnOptions}
      onCancel={onComplete}
      onSubmit={handleFormSubmit}
    />
  );
};

export default JournalEntryFormContainer;
